import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  Select,
  MenuItem,
  Typography,
  CircularProgress
} from '@mui/material';
import { Box } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { setLanguage, selectCurrentLanguage, selectIsLanguageChanging } from '../redux/slices/languageSlice';

// UK Flag Component
const UKFlag = ({ style }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30" style={style}>
    <rect width="60" height="30" fill="#012169"/>
    <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
    <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
    <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="4"/>
    <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
  </svg>
);

// Netherlands Flag Component
const NetherlandsFlag = ({ style }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 600" style={style}>
    <rect width="900" height="600" fill="#AE1C28"/>
    <rect width="900" height="400" y="200" fill="#FFFFFF"/>
    <rect width="900" height="200" y="400" fill="#21468B"/>
  </svg>
);

const LanguageSelector = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const currentLanguage = useSelector(selectCurrentLanguage);
  const isChanging = useSelector(selectIsLanguageChanging);

  const handleLanguageChange = (event) => {
    const selectedLanguage = event.target.value;
    dispatch(setLanguage(selectedLanguage));
  };

  // Language options with flags - keep native language names for better UX
  const languageOptions = [
    { code: 'en', flag: UKFlag },
    { code: 'nl', flag: NetherlandsFlag }
  ];

  return (
    <FormControl 
      variant="standard" 
      sx={{ 
        minWidth: '50px',
        width: '50px',
        marginRight: '8px',
        '& .MuiSelect-select': {
          padding: '2px 8px',
          fontSize: '10.5px',
          fontWeight: 600,
          fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif',
          color: 'rgb(53, 53, 53)',
          display: 'flex',
          alignItems: 'center',
          height: '20px',
          lineHeight: '20px',
        },
        '& .MuiInput-underline:before': {
          borderBottom: 'none',
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
          borderBottom: 'none',
        },
        '& .MuiInput-underline:after': {
          borderBottom: 'none',
        },
        '& .MuiSelect-icon': {
          fontSize: '16px',
          color: 'rgb(53, 53, 53)',
        }
      }}
    >
      <Select
        value={currentLanguage}
        onChange={handleLanguageChange}
        disableUnderline
        disabled={isChanging}
        renderValue={(value) => {
          const selectedOption = languageOptions.find(option => option.code === value);
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {isChanging && (
                <CircularProgress
                  size={12}
                  sx={{ color: 'rgb(53, 53, 53)' }}
                />
              )}
              {selectedOption && (
                <selectedOption.flag style={{ width: '20px', height: '15px' }} />
              )}
            </div>
          );
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: 0,
              boxShadow: '0px 4px 16px rgba(0,0,0,0.1)',
              minWidth: '140px',
              '& .MuiMenuItem-root': {
                fontSize: '11px',
                fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif',
                padding: '8px 12px',
                '&:hover': {
                  backgroundColor: 'rgba(43, 128, 182, 0.08)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(43, 128, 182, 0.12)',
                  '&:hover': {
                    backgroundColor: 'rgba(43, 128, 182, 0.16)',
                  },
                },
              },
            },
          },
        }}
      >
        {languageOptions.map((option) => {
          const FlagComponent = option.flag;
          return (
            <MenuItem key={option.code} value={option.code}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FlagComponent style={{ width: '20px', height: '15px' }} />
              </div>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default LanguageSelector;
